import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, FadeIn } from "../components/Reveal";
import { SERVICES, IMAGES } from "../lib/brand";
import { setSeo } from "../lib/seo";
import { MetaLabel } from "../components/Bits";

export default function Services() {
  useEffect(() => { setSeo({ title: "Estate Management Services · Marella Bolognesi — Miami, Florida", description: "Private Estate Management in Miami: property maintenance, staff management, budgeting, home systems, residence oversight and preventive inspections — coordinated with discretion." }); }, []);

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <img src={IMAGES.mission} alt="" className="absolute right-0 top-0 h-full w-full lg:w-3/5 object-cover opacity-[0.14]" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/85 to-bg/40" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12 pt-20 lg:pt-28 pb-16">
          <FadeIn><MetaLabel>Estate Management · Miami, Florida</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-6 font-serif font-light text-ink text-5xl sm:text-6xl lg:text-[6.5vw] leading-[0.95] tracking-tight max-w-5xl">
              A well-run home is built on what you don't see.
            </h1>
          </FadeIn>
          <FadeIn delay={0.16}>
            <p className="mt-10 max-w-xl text-lg font-light text-ink-soft leading-relaxed">
              Private Estate Management in Miami — bringing structure, oversight and continuity to the people, properties and systems that keep a home running well.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Feature image */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <FadeIn delay={0.1}>
          <div className="overflow-hidden">
            <img src={IMAGES.miamiEstate} alt="Miami luxury estate" className="w-full aspect-[16/8] object-cover" />
          </div>
        </FadeIn>
      </section>

      {/* What Estate Management means */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
          <Reveal className="lg:col-span-4"><MetaLabel>What Estate Management Means</MetaLabel></Reveal>
          <div className="lg:col-span-8 max-w-2xl space-y-8 text-lg font-light text-ink-soft leading-relaxed">
            <Reveal>
              <p>
                Estate Management is the coordination of the people, systems, vendors and details required to operate a private residence at a consistently high standard.
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <p>
                The role is part operations, part leadership and part anticipation: creating structure so the household can function smoothly without requiring the principal to manage what happens behind it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-24 lg:pb-32">
        <Reveal><MetaLabel className="mb-14">What Estate Management Encompasses</MetaLabel></Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={(i % 3) * 0.08} className="border border-line">
              <div data-testid={`service-${s.n}`} className="p-8 lg:p-10 h-full group hover:bg-surface transition-colors">
                <span className="font-serif text-3xl text-accent">{s.n}</span>
                <h3 className="mt-6 font-serif text-2xl lg:text-3xl font-normal text-ink">{s.title}</h3>
                <p className="mt-4 text-sm text-ink-soft font-light leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Discreet principals section */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
            <Reveal className="lg:col-span-4"><MetaLabel>For Principals &amp; Family Offices</MetaLabel></Reveal>
            <div className="lg:col-span-8 max-w-2xl">
              <Reveal>
                <h2 className="font-serif font-light text-3xl lg:text-5xl text-ink leading-[1.1] tracking-tight">
                  A single, discreet point of trust for the running of a home.
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-8 text-lg font-light text-ink-soft leading-relaxed">
                  Selected private engagements are considered on a confidential basis — whether establishing structure in a new residence, restoring order to an existing one, or providing continuity across properties and staff.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <div className="mt-10 grid sm:grid-cols-3 gap-8 border-t border-line pt-8">
                  {[
                    { k: "Discretion", v: "Confidentiality and judgment, always." },
                    { k: "Continuity", v: "Systems and standards that endure." },
                    { k: "Selective", v: "A limited number of private engagements." },
                  ].map((b) => (
                    <div key={b.k}>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-accent">{b.k}</p>
                      <p className="mt-3 text-sm font-light text-ink-soft leading-relaxed">{b.v}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <Link to="/contact" data-testid="principals-cta" className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
                  Arrange a private conversation <ArrowRight size={14} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Approach split */}
      <section className="border-t border-line bg-ink text-bg">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-24 lg:py-40 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <p className="text-[11px] uppercase tracking-ultra text-bg/50 mb-8">The Approach</p>
            <h2 className="font-serif font-light text-4xl lg:text-6xl leading-[1.02] tracking-tight">
              Structure matters. Trust matters more.
            </h2>
            <p className="mt-8 text-lg font-light text-bg/70 leading-relaxed max-w-lg">
              Estate Management is deeply personal work. It requires access, discretion and an understanding of how a principal prefers to live. My role is to create the structure around those preferences — and manage the details with consistency, judgment and care.
            </p>
            <Link to="/contact" data-testid="services-enquire-cta" className="mt-10 inline-flex items-center gap-3 bg-bg text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors group">
              Arrange a private conversation <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="overflow-hidden">
              <img src={IMAGES.interior} alt="Refined interior" className="w-full aspect-[4/5] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
